import { DeleteFileRequest, UploadFileRequest } from '@/DTOs/files';
import {
  BusinessInfo,
  Customer,
  CustomerDocuments,
  isCustomerDocumentType,
  patchLegalRepresentativeDocument,
  patchOwnerDocument,
} from '@/types/customer';

type AnyDocument = '';
type WriteableDraft<T> = { -readonly [P in keyof T]: T[P] };

const extractDocumentId = (path: string | undefined): keyof CustomerDocuments | AnyDocument => {
  switch (path) {
    case 'documents/cnpjRegistration':
      return 'cnpjRegistration';
    case 'documents/incorporation':
      return 'incorporation';
    case 'documents/powerOfAttorney':
      return 'powerOfAttorney';
    case 'documents/bankAccountStatement':
      return 'bankAccountStatement';
    default:
      return '';
  }
};

export const isLegalRepresentativeFilePath = (path: string | undefined): boolean =>
  path === 'legalRepresentative/files';

const isOwnerFilePath = (path: string | undefined): boolean => {
  if (!path) {
    return false;
  }
  return ownerFilePathRegExp.test(path);
};

const ownerFilePathRegExp = new RegExp('owners/([^/]+)/files');

export const isCustomerFilePath = (path: string | undefined): boolean => {
  return (
    isCustomerDocumentType(extractDocumentId(path)) ||
    isLegalRepresentativeFilePath(path) ||
    isOwnerFilePath(path)
  );
};

const patchCustomerFile = (
  draft: WriteableDraft<Customer>,
  path: string,
  file: File | null,
): void => {
  if (isCustomerDocumentType(extractDocumentId(path))) {
    const businessInfo: BusinessInfo = draft.businessInfo ?? BusinessInfo.empty();
    const documents = businessInfo.documents ?? CustomerDocuments.empty();
    draft.businessInfo = {
      ...businessInfo,
      documents: {
        ...documents,
        [extractDocumentId(path)]: file?.name ?? null,
      },
    };
  } else if (path === 'legalRepresentative/files') {
    const businessInfo: BusinessInfo = draft.businessInfo ?? BusinessInfo.empty();
    draft.businessInfo = patchLegalRepresentativeDocument(businessInfo, file?.name ?? null);
  } else if (ownerFilePathRegExp.test(path)) {
    const businessInfo: BusinessInfo = draft.businessInfo ?? BusinessInfo.empty();
    const matches = ownerFilePathRegExp.exec(path);
    const ownerId = matches?.[1];

    draft.businessInfo = patchOwnerDocument(businessInfo, ownerId, file?.name ?? null);
  } else {
    console.debug(`warning: unhandled file upload ${path}`);
  }
};

export const customerFileUploadPatcher =
  (request: UploadFileRequest) =>
  (draft: WriteableDraft<Customer>): void =>
    patchCustomerFile(draft, request.path, request.file);

export const customerFileRemovePatcher =
  (request: DeleteFileRequest) =>
  (draft: WriteableDraft<Customer>): void =>
    patchCustomerFile(draft, request.path, null);
